export type ErrorSource = 'vue' | 'promise' | 'window' | 'axios'

export interface NormalizedError {
  source: ErrorSource
  message: string
  reason: unknown
  stack: string | null
  url?: string
  line?: number
  axiosCode?: string
  axiosStatus?: number
  axiosOriginalMessage?: string
}

interface NoiseFilter {
  name: string
  appliesTo: readonly ErrorSource[]
  test: (err: NormalizedError) => boolean
}

// Cross-origin script errors in Chrome arrive as the literal "Script error."
// with no url/line/error object — the browser strips the source for privacy
// when the script isn't served with CORS headers. iOS Safari's equivalent is
// `webkit-masked-url://hidden/` frames, handled by `safariMaskedCrossOrigin`.
const chromeCrossOriginMasked: NoiseFilter = {
  name: 'chromeCrossOriginMasked',
  appliesTo: ['window'],
  test: ({ message, url, line, reason }) =>
    !(reason instanceof Error) &&
    message === 'Script error.' &&
    !url &&
    (line === undefined || line === 0)
}

// Safari masks the source URL of cross-origin scripts whose responses lack
// CORS headers (`webkit-masked-url://hidden/`). When every frame in the
// rejection stack is masked, the throw originated outside our bundle —
// equivalent to Chrome's `Script error.` cross-origin masking above.
const safariMaskedCrossOrigin: NoiseFilter = {
  name: 'safariMaskedCrossOrigin',
  appliesTo: ['promise'],
  test: ({ reason }) => {
    if (!(reason instanceof Error) || !reason.stack) return false
    const frames = reason.stack
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && line.includes('@'))
    if (frames.length === 0) return false
    return frames.every((frame) => frame.includes('webkit-masked-url://hidden/'))
  }
}

// WebKit fires `TypeError: Load failed` (no stack) for native fetch/preload
// failures — backgrounded tabs on cellular, modulepreload misses, mid-flight
// navigation aborts. No JS frames means no actionable signal.
const safariNetwork: NoiseFilter = {
  name: 'safariNetwork',
  appliesTo: ['promise'],
  test: ({ message, reason }) => {
    if (message !== 'Load failed') return false
    return !(reason instanceof Error) || !reason.stack
  }
}

// Microsoft Outlook / Office 365 Safe Links scans pages it crawls and injects
// a script that throws this exact signature into the page context. The errors
// do not originate from our bundle.
const outlookSafeLinks: NoiseFilter = {
  name: 'outlookSafeLinks',
  appliesTo: ['vue', 'promise', 'window'],
  test: ({ message }) =>
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(message)
}

// A third-party script (browser extension / injected vendor pixel) filters
// events by `type === 'ParityDigital'` and throws when its target collection
// is undefined. The 'ParityDigital' literal does not appear anywhere in our
// bundle.
const parityDigital: NoiseFilter = {
  name: 'parityDigital',
  appliesTo: ['vue', 'promise', 'window'],
  test: ({ message }) => message.includes("'ParityDigital'") || message.includes('"ParityDigital"')
}

// Manually-verified third-party rejection: "null is not an object (evaluating
// 'o.id')" fires across multiple CADY apps on different domains (booking and
// shop), so it cannot originate in either bundle. Safari often strips the
// stack of cross-origin rejections, leaving only our handler frames in NR —
// the message signature is the only reliable identifier.
const thirdPartyOId: NoiseFilter = {
  name: 'thirdPartyOId',
  appliesTo: ['promise', 'window'],
  test: ({ message }) => message.includes("null is not an object (evaluating 'o.id')")
}

// Benign browser warning emitted when a ResizeObserver callback triggers
// further layout changes the browser cannot deliver in the same frame.
// Recovers on the next frame.
const resizeObserverLoop: NoiseFilter = {
  name: 'resizeObserverLoop',
  appliesTo: ['window'],
  test: ({ message }) => message.includes('ResizeObserver loop')
}

// Post-deploy stale-chunk errors: the user's open tab references a Vite
// bundle hash that no longer exists on the CDN. Lazy route/component imports
// and CSS modulepreloads fail with these signatures. The user recovers on
// refresh; nothing in our bundle is broken.
const staleChunk: NoiseFilter = {
  name: 'staleChunk',
  appliesTo: ['promise'],
  test: ({ message }) =>
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS for') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module')
}

// Network errors are typically user-side (poor connectivity, iOS
// backgrounding) — not actionable engineering signal.
const axiosNetwork: NoiseFilter = {
  name: 'axiosNetwork',
  appliesTo: ['axios'],
  test: ({ axiosCode, axiosOriginalMessage }) =>
    axiosCode === 'ERR_NETWORK' || axiosOriginalMessage === 'Network Error'
}

// Axios rejects with `Request aborted` (code `ECONNABORTED`) when the XHR is
// aborted mid-flight — page navigation, user-cancelled requests, or external
// scanners abandoning the page. Real timeouts surface as `timeout of Xms
// exceeded` and are NOT filtered.
const axiosAbort: NoiseFilter = {
  name: 'axiosAbort',
  appliesTo: ['axios'],
  test: ({ axiosCode, axiosOriginalMessage }) =>
    axiosCode === 'ECONNABORTED' && axiosOriginalMessage === 'Request aborted'
}

// Business-validation 4xx responses surface to the user via toast and are not
// actionable engineering signal. Match on known server-emitted messages.
const expectedAxiosValidation: NoiseFilter = {
  name: 'expectedAxiosValidation',
  appliesTo: ['axios'],
  test: ({ message }) => /You must select at least \d+ items from the .+ Scene group/.test(message)
}

const NOISE_FILTERS: readonly NoiseFilter[] = [
  chromeCrossOriginMasked,
  safariMaskedCrossOrigin,
  safariNetwork,
  outlookSafeLinks,
  parityDigital,
  thirdPartyOId,
  resizeObserverLoop,
  staleChunk,
  axiosNetwork,
  axiosAbort,
  expectedAxiosValidation
]

export function isNoise(err: NormalizedError): boolean {
  return NOISE_FILTERS.some((filter) => filter.appliesTo.includes(err.source) && filter.test(err))
}
